import { AriaAttributes, PropsWithChildren, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { __useInternalDragAndDrop } from './Context/DragAndDropContext';

const Container = styled.div`
  cursor: grab;
`;

export interface DraggableProps extends AriaAttributes {
  /** Used to identify the draggable item being dropped. */
  dragId: string;
  /** Fired when a draggable item is dropped into a droppable zone. */
  onDrop: (id?: string) => void;
  /** Fired when a draggable item is dragged.  */
  onDrag?: (event?: DragEvent) => void;
  /** Fired when a draggable item is dragging.  */
  onDragging?: (event?: DragEvent) => void;
  /** Fired when a draggable item is dropped in a location that is not designated as droppable. */
  onDropFallback?: () => void;
  /** Fired when a draggable item has been dropped at any time. */
  onDragEnd?: () => void;
}

export function Draggable({
  dragId,
  onDrop,
  onDrag,
  onDragging,
  children,
  onDropFallback,
  ...props
}: PropsWithChildren<DraggableProps>) {
  const { registerDraggable, setDropFallback } = __useInternalDragAndDrop();

  const onDropFallbackCallback = useCallback(() => {
    if (onDropFallback) {
      onDropFallback();
    }
  }, [onDropFallback]);

  useEffect(() => {
    if (!dragId) {
      throw new Error('"dragId" is required in order to register a new draggable.');
    }

    registerDraggable(dragId, onDrop);
    setDropFallback(onDropFallbackCallback);
  }, [dragId, onDrop, onDropFallbackCallback, registerDraggable, setDropFallback]);

  const onDragStart = useCallback(
    (event: DragEvent, type: string) => {
      event.dataTransfer.setData('text/plain', type);
      event.dataTransfer.effectAllowed = 'move';
      if (onDrag) {
        onDrag();
      }
    },
    [onDrag],
  );

  const onDragHandle = useCallback(
    (event: DragEvent) => {
      if (onDragging) {
        onDragging(event);
      }
    },
    [onDragging],
  );

  return (
    <Container
      draggable
      onDragStart={event => onDragStart((event as unknown) as DragEvent, dragId)}
      onDrag={event => onDragHandle((event as unknown) as DragEvent)}
      {...props}
    >
      {children}
    </Container>
  );
}
